import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { clientsService } from '../services/logistics/clients';
import { shipmentsService } from '../services/logistics/shipments';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatting';

const Clients = () => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to get translated status
  const getStatusText = (status) => {
    const statusMap = {
      'active': t('common.active'),
      'inactive': t('common.inactive'),
      'pending': t('common.pending')
    };
    return statusMap[status] || status;
  };

  // Handle favorite toggle
  const handleToggleFavorite = async (clientId, isFavorite, e) => {
    e.stopPropagation();
    try {
      await clientsService.updateClient(clientId, { isFavorite: !isFavorite });
      setClients(prevClients => 
        prevClients.map(client => 
          client.id === clientId 
            ? { ...client, isFavorite: !isFavorite }
            : client
        )
      );
      toast.success(
        !isFavorite 
          ? t('clients.added_to_favorites') 
          : t('clients.removed_from_favorites')
      );
    } catch (error) {
      console.error('Error updating favorite status:', error);
      toast.error(t('clients.favorite_update_failed'));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentCompany?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch clients and shipments in parallel
        const [clientsData, shipmentsData] = await Promise.all([
          clientsService.getClients(currentCompany.id),
          shipmentsService.getShipments(currentCompany.id)
        ]);
        
        // Calculate real statistics for each client
        const clientsWithStats = clientsData.map(client => {
          const clientShipments = shipmentsData.filter(s => s.clientId === client.id);
          const totalRevenue = clientShipments.reduce((sum, s) => sum + (s.cost || 0), 0);
          const totalShipments = clientShipments.length;
          
          // Get the most recent shipment date for sorting
          const lastShipmentDate = clientShipments.length > 0 
            ? Math.max(...clientShipments.map(s => new Date(s.createdAt?.toDate ? s.createdAt.toDate() : s.createdAt).getTime()))
            : 0;

          const lastShipment = clientShipments.length > 0 
            ? new Date(lastShipmentDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : null;

          return {
            ...client,
            totalShipments,
            totalRevenue,
            lastShipment,
            lastShipmentDate,
            status: client.status || 'active',
            isFavorite: client.isFavorite || false
          };
        });

        // Sort clients: favorites first, then by last shipment date (most recent first)
        const sortedClients = clientsWithStats.sort((a, b) => {
          // First sort by favorite status
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          
          // Then sort by last shipment date (most recent first)
          return b.lastShipmentDate - a.lastShipmentDate;
        });
        
        setClients(sortedClients);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('clients.failed_to_load'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentCompany?.id]);

  const handleDeleteClient = async (clientId, clientName) => {
    if (window.confirm(t('clients.confirm_delete', { clientName }))) {
      try {
        await clientsService.deleteClient(clientId);
        setClients(prev => prev.filter(c => c.id !== clientId));
        toast.success(t('clients.deleted_successfully'));
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error(t('clients.delete_failed'));
      }
    }
  };

  const handleCardClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const filteredClients = clients.filter(client =>
    client.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate real statistics
  const totalRevenue = clients.reduce((sum, client) => sum + (client.totalRevenue || 0), 0);
  const totalShipments = clients.reduce((sum, client) => sum + (client.totalShipments || 0), 0);
  const avgShipmentValue = totalShipments > 0 ? totalRevenue / totalShipments : 0;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const newThisMonth = clients.filter(client => {
    if (!client.createdAt) return false;
    const createdDate = client.createdAt.toDate ? client.createdAt.toDate() : new Date(client.createdAt);
    return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
  }).length;

  const statistics = [
    {
      title: t('clients.active_clients'),
      value: clients.filter(c => c.status === 'active').length,
      icon: UsersIcon,
      color: 'blue',
    },
    {
      title: t('clients.new_this_month'),
      value: newThisMonth,
      icon: CalendarIcon,
      color: 'green',
    },
    {
      title: t('clients.total_revenue'),
      value: formatCurrency(totalRevenue),
      icon: CurrencyDollarIcon,
      color: 'purple',
    },
    {
      title: t('clients.avg_shipment_value'),
      value: formatCurrency(Math.round(avgShipmentValue)),
      icon: TruckIcon,
      color: 'orange',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('clients.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('clients.manage_relationships')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/clients/create"
            className="btn btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t('clients.add_client')}
          </Link>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statistics.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600',
            green: 'bg-green-50 text-green-600',
            purple: 'bg-purple-50 text-purple-600',
            orange: 'bg-orange-50 text-orange-600',
          };

          return (
            <div key={index} className="card !p-3 sm:!p-4">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="ml-3 rtl:mr-3 rtl:ml-0">
                  <p className="text-xs font-medium text-gray-600">{stat.title}</p>
                  <p className="text-xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="card !p-3 sm:!p-4">
        <div className="relative max-w-lg">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={t('clients.search_placeholder')}
            className="input-field pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {filteredClients.map((client) => (
          <div
            key={client.id}
            className={`card !p-3 sm:!p-4 hover:shadow-lg transition-shadow duration-200 cursor-pointer ${
              client.isFavorite ? 'ring-2 ring-yellow-400 bg-yellow-50' : ''
            }`}
            onClick={() => handleCardClick(client.id)}
          >
            {/* Quick Created Warning */}
            {client.isQuickCreated && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-2 mb-3">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-400 mr-2 rtl:ml-2 rtl:mr-0 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    <strong>{t('clients.incomplete_information')}</strong>
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <UsersIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{client.companyName}</h3>
                    {client.isFavorite && (
                      <StarIconSolid className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{client.contactPerson}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => handleToggleFavorite(client.id, client.isFavorite, e)}
                  className="p-1 rounded-full hover:bg-gray-100"
                  title={client.isFavorite ? t('clients.remove_from_favorites') : t('clients.add_to_favorites')}
                >
                  {client.isFavorite ? (
                    <StarIconSolid className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <StarIcon className="w-4 h-4 text-gray-400 hover:text-yellow-500" />
                  )}
                </button>
                <span className={`badge ${client.status === 'active' ? 'badge-success' : 'badge-gray'}`}>
                  {getStatusText(client.status)}
                </span>
              </div>
            </div>

            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 flex-shrink-0">{t('clients.email')}</span>
                <span className="font-medium text-gray-900 truncate">{client.email || '—'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 flex-shrink-0">{t('clients.phone')}</span>
                <span className="font-medium text-gray-900 truncate">{client.phone || '—'}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500 flex-shrink-0">{t('clients.address')}</span>
                <span className="font-medium text-gray-900 truncate">
                  {client.address || client.city || t('clients.no_address')}
                </span>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('clients.total_shipments')}</p>
                <p className="text-sm font-semibold text-gray-900">{client.totalShipments || 0}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('clients.last_shipment')}</p>
                <p className="text-xs font-medium text-gray-900 truncate">
                  {client.lastShipment || '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-gray-500">{t('clients.total_revenue')}</p>
                <p className="text-sm font-semibold text-green-600 truncate">
                  {formatCurrency(client.totalRevenue || 0)}
                </p>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              {/* Complete Information Button for quick-created clients */}
              {client.isQuickCreated && (
                <div className="mb-2">
                  <Link
                    to={`/clients/${client.id}/edit`}
                    className="w-full btn btn-warning text-xs inline-flex items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CheckCircleIcon className="w-3.5 h-3.5 mr-1" />
                    {t('clients.complete_information')}
                  </Link>
                </div>
              )}
              
              <div className="flex items-center gap-1.5">
                <Link
                  to={`/clients/${client.id}`}
                  className="flex-1 btn btn-outline text-xs !py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EyeIcon className="w-3.5 h-3.5 mr-1 rtl:ml-1 rtl:mr-0" />
                  {t('clients.view')}
                </Link>
                <Link
                  to={`/clients/${client.id}/edit`}
                  className="text-green-600 hover:text-green-900 p-1.5"
                  title={t('clients.edit')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                </Link>
                <button
                  className="text-red-600 hover:text-red-900 p-1.5"
                  title={t('clients.delete')}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClient(client.id, client.companyName);
                  }}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('clients.no_clients_found')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('clients.get_started_message')}
          </p>
          <div className="mt-6">
            <Link
              to="/clients/create"
              className="btn btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              {t('clients.add_client')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients; 